import React, { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { to10Digit } from "@/lib/phone";

/**
 * PhoneInput10 — single source of truth for collecting Indian mobile
 * numbers in the UI. Shows a sticky "+91" chip on the left so users
 * never have to type the country code, and clamps input to exactly
 * 10 numeric digits. `onChange` always emits the cleaned 10-digit
 * string (or shorter while typing).
 *
 * Storing 10-digit strings in form state keeps everything consistent
 * across the app; convert with `toE164()` from `@/lib/phone` only at
 * the request boundary (e.g. axios payload).
 *
 * Accepts a `locked` prop for invite-prefilled flows where the phone
 * is sales-verified and cannot be edited (renders read-only).
 */
export default function PhoneInput10({
    value,
    onChange,
    placeholder = "98765 43210",
    locked = false,
    disabled = false,
    className = "",
    inputClassName = "",
    testid,
    autoFocus = false,
    id,
    name,
    onBlur,
}) {
    const displayValue = useMemo(() => to10Digit(value), [value]);

    const handleChange = (e) => {
        // Strip non-digits and clamp to 10 chars in one pass
        const cleaned = e.target.value.replace(/\D/g, "").slice(0, 10);
        onChange?.(cleaned);
    };

    return (
        <div className={`relative flex items-stretch ${className}`}>
            <span
                className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-secondary/60 text-sm font-mono font-medium text-muted-foreground select-none"
                aria-hidden="true"
            >
                +91
            </span>
            <Input
                id={id}
                name={name}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                value={displayValue}
                onChange={handleChange}
                onBlur={onBlur}
                placeholder={placeholder}
                readOnly={locked}
                disabled={disabled || locked}
                autoFocus={autoFocus}
                className={`rounded-l-none flex-1 ${inputClassName}`}
                data-testid={testid}
                aria-label="10-digit Indian mobile number"
            />
        </div>
    );
}
